/**
 * Data validation for database inserts.
 * Each function throws a descriptive error if validation fails.
 */

function requireString(value, field) {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${field} is required and must be a non-empty string`);
  }
}

function requirePositiveNumber(value, field) {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (typeof n !== "number" || !isFinite(n) || n < 0) {
    throw new Error(`${field} must be a non-negative number, got: ${value}`);
  }
}

function requireTimestamp(value, field) {
  if (typeof value === "number" && value > 0) return;
  if (typeof value === "string" && !isNaN(Date.parse(value))) return;
  throw new Error(`${field} must be a valid timestamp, got: ${value}`);
}

function requireDate(value, field) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error(`${field} must be a date string (YYYY-MM-DD), got: ${value}`);
  }
}

function requireId(value, field) {
  if (!value || typeof value !== "string") {
    throw new Error(`${field} is required`);
  }
}

// ---- Public validators ----

export function validateWorkout(workout) {
  requireId(workout.id, "workout.id");
  requireString(workout.name, "workout.name");
  requireTimestamp(workout.startTime, "workout.startTime");
  requireTimestamp(workout.endTime, "workout.endTime");

  if (!Array.isArray(workout.exerciseBlocks)) {
    throw new Error("workout.exerciseBlocks must be an array");
  }
  if (workout.exerciseBlocks.length === 0) {
    throw new Error("Workout must have at least one exercise");
  }

  // Ensure endTime is after startTime
  const start = typeof workout.startTime === "number" ? workout.startTime : Date.parse(workout.startTime);
  const end = typeof workout.endTime === "number" ? workout.endTime : Date.parse(workout.endTime);
  if (end < start) {
    throw new Error("workout.endTime must be after workout.startTime");
  }
}

export function validateMealEntry(date, entry) {
  requireDate(date, "date");
  requireId(entry.id, "entry.id");
  requireId(entry.mealId, "entry.mealId");

  const servings = parseFloat(entry.servings);
  if (!isFinite(servings) || servings <= 0) {
    throw new Error(`Servings must be a positive number, got: ${entry.servings}`);
  }
}

export function validateCustomMeal(meal) {
  requireId(meal.id, "meal.id");
  requireString(meal.name, "meal.name");
  requirePositiveNumber(meal.calories, "meal.calories");
  requirePositiveNumber(meal.protein, "meal.protein");
  requirePositiveNumber(meal.carbs, "meal.carbs");
  requirePositiveNumber(meal.fat, "meal.fat");
}

export function validateRun(run) {
  requireId(run.id, "run.id");
  requirePositiveNumber(run.distance, "run.distance");
  if (parseFloat(run.distance) <= 0) {
    throw new Error("run.distance must be greater than zero");
  }
  requirePositiveNumber(run.duration, "run.duration");
  requireTimestamp(run.timestamp, "run.timestamp");

  const validUnits = ["mi", "km"];
  if (!validUnits.includes(run.unit)) {
    throw new Error(`run.unit must be one of: ${validUnits.join(", ")}`);
  }
}

export function validateWeightEntry(entry) {
  requireId(entry.id, "entry.id");
  requirePositiveNumber(entry.weight, "entry.weight");

  const w = parseFloat(entry.weight);
  if (w <= 0 || w > 1500) {
    throw new Error(`Weight must be between 0 and 1500, got: ${w}`);
  }

  requireDate(entry.date, "entry.date");
}

export function validateServings(servings) {
  const s = parseFloat(servings);
  if (!isFinite(s) || s <= 0) {
    throw new Error(`Servings must be a positive number, got: ${servings}`);
  }
}
